import {
  DrawShapeUtil,
  Editor,
  Geometry2d,
  ShapeUtil,
  TLDrawShape,
  SVGContainer,
  rng,
  TLDrawShapeSegment,
  Vec,
  SvgExportContext,
  TLResizeInfo,
  toFixed,
  useEditor,
  JsonObject,
  TLShapeId,
  VecLike,
  TLPageId,
  TLUnknownShape,
  useValue,
} from "tldraw"
import { last } from "../util/array"
import { svgInk, svgInkForSegments } from "./shared/freehand/svgInk"
import { getFreehandOptions } from "./draw/getPath"
import { getSvgPathFromMultipleSegments } from "./shared/freehand/svg"
import {
  getStrokePoints,
  getStrokePointsFromSegments,
} from "./shared/freehand/getStrokePoints"

import { getSvgPathFromStrokePoints } from "./shared/freehand/svg"

import { LDPage, LDPageMeta } from "../../shared/LDPage"
import { Color, Expr } from "../../shared/Expr"
import {
  CloneOptions,
  CustomDrawShape,
  CustomDrawShapeProps,
  customDrawShapeProps,
} from "../../shared/CustomDrawShape"
import { LiveDraw } from "../LiveDraw"
import { defaultTransformation } from "../canvas-ui/TransformationButton"
import { JSX, useEffect } from "react"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { transforming } from "../canvas-ui/SelectionToolbar"
import { LDGroupShape, LDShape } from "../../shared/LDShape"
import { STROKE_SIZES } from "../shared/default-shape-constants"
import { LDInstanceMeta } from "../../shared/LDInstance"
import { flattenParams } from "./flattenParams"
import { assert } from "../upstream/control"
import { Layer } from "../../shared/Layer"
import { getFillDefForExport } from "./defaultStyleDefs"
import { StrokeOptions } from "./shared/freehand/types"
import React from "react"

export let changingBaseOrientation = false

function getDot(point: VecLike, sw: number) {
  const r = (sw + 1) * 0.5
  return `M ${point.x} ${point.y} m -${r}, 0 a ${r},${r} 0 1,0 ${r * 2},0 a ${r},${r} 0 1,0 -${
    r * 2
  },0`
}
export function indicator(isFill: boolean, shape: TLDrawShape, editor: Editor) {
  const sw = (STROKE_SIZES[shape.props.size] + 1) * shape.props.scale

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const forceSolid = useValue(
    "force solid",
    () => {
      const zoomLevel = editor.getZoomLevel()
      return zoomLevel < 0.5 && zoomLevel < 1.5 / sw
    },
    [editor, sw],
  )

  const showAsComplete =
    shape.props.isComplete || last(shape.props.segments)?.type === "straight"

  const options: StrokeOptions = isFill
    ? {
        thinning: 0,
        streamline: 0,
        smoothing: 0,
        //size: shape.props.radius * shape.props.scale * 2.65,
      }
    : getFreehandOptions(shape.props, sw, showAsComplete, true)

  //const options =

  // Use the original segments (which are grouped into paths/rings)
  const { segments } = shape.props

  // Convert each segment group into a path independently
  const paths = segments.map((segmentGroup) => {
    const points = getPointsFromSegments([segmentGroup])
    if (points.length < 1) return ""

    const strokePoints = getStrokePoints(points, options)
    return strokePoints.length > 1
      ? getSvgPathFromStrokePoints(strokePoints, shape.props.isClosed)
      : getDot(points[0], sw)
  })

  return (
    <>
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </>
  )
}

export class CustomDrawShapeUtil extends ShapeUtil<CustomDrawShape> {
  static override type = "custom-draw" as const
  static override props = customDrawShapeProps
  // static override migrations = drawShapeMigrations

  underlying: DrawShapeUtil

  constructor(editor: Editor) {
    super(editor)

    // delegate to DrawShapeUtil for geometry stuff
    this.underlying = new DrawShapeUtil(editor)
  }

  getDefaultProps(): CustomDrawShapeProps {
    const isFill = ["draw-fill", "marker", "bucket"].includes(
      this.editor.getCurrentToolId(),
    )
    const instanceMeta = this.editor.getInstanceState().meta as LDInstanceMeta

    const currentPage = this.editor.getCurrentPage() as LDPage
    return {
      ...(isFill
        ? {
            isFill: true,
            parameterValues: {
              fillColor: Expr.lit(instanceMeta.fillColor),
              visible: Expr.lit(true),
              opacity: Expr.lit(1),
            },
          }
        : {
            isFill: false,
            parameterValues: {
              strokeColor: Expr.lit(instanceMeta.strokeColor),
              strokeWidth: Expr.lit(instanceMeta.strokeWidth),
              visible: Expr.lit(true),
              opacity: Expr.lit(1),
            },
          }),
      segments: [],
      isComplete: false,
      isPen: false,
      scale: 1,
      layer: currentPage.meta.currentLayer,
      transform: defaultTransformation,
    }
  }

  getGeometry(shape: CustomDrawShape): Geometry2d {
    const pageId = LDShape.getSourcePageId(this.editor, shape)
    const shapePage = this.editor.getPage(pageId) as LDPage
    const shapeLayerProps = shapePage.meta.layers[shape.props.layer]

    if (!shapeLayerProps) {
      throw `Props missing for layer [${shape.props.layer}] in ${JSON.stringify(shapePage.meta)}`
    }

    const proxyShape: TLDrawShape = toProxyShape(shape, shape.props.isFill)

    return this.underlying.getGeometry(proxyShape)
  }
  component(shape: CustomDrawShape) {
    const pageId = LDShape.getSourcePageId(this.editor, shape)
    const pageMeta = (this.editor.getPage(pageId)! as LDPage).meta
    const layer = pageMeta.layers[shape.props.layer]

    assert(layer, `Can't find ${shape.props.layer} in ${pageMeta.layers}`)

    const params = flattenParams(shape, this.editor)
    const opacity =
      Expr.evaluate(layer.opacity, params) *
      Expr.evaluate(shape.props.parameterValues.opacity, params)

    const adjustedOpacity = !pageMeta.focusedLayerId
      ? opacity
      : pageMeta.focusedLayerId === shape.props.layer
        ? opacity + (1 - opacity) / 2
        : opacity * 0.3

    const isHovered =
      this.editor.getCurrentToolId() === "bucket" &&
      this.editor.getHoveredShapeId() === shape.id

    //if (!Expr.evaluate(layer.visible, params)) return null

    const visibleExpression =
      layer.layerOverrides?.visible ?? shape.props.parameterValues.visible

    //if (!Expr.evaluate(visibleExpression, params)) return null

    return (
      <SVGContainer>
        <CustomDrawShapeSvg
          pageId={pageId}
          hovered={isHovered}
          isFill={shape.props.isFill}
          shape={shape}
          color={Expr.evaluate(
            shape.props.isFill
              ? (layer.layerOverrides?.fillColor ??
                  shape.props.parameterValues.fillColor)
              : (layer.layerOverrides?.strokeColor ??
                  shape.props.parameterValues.strokeColor),
            params,
          )}
          opacity={!Expr.evaluate(layer.visible, params) ? 0 : adjustedOpacity}
        />
      </SVGContainer>
    )
  }
  indicator(shape: CustomDrawShape): JSX.Element {
    const isFill = shape.props.isFill
    return indicator(isFill, toProxyShape(shape, isFill), this.editor)
  }

  override toSvg(shape: CustomDrawShape, ctx: SvgExportContext) {
    const pageId = LDShape.getSourcePageId(this.editor, shape)

    const parameterValues = (
      this.editor.getShape(shape.parentId) as LDGroupShape
    )?.props.parameterValues

    ctx.addExportDef(getFillDefForExport(/*shape.props.fill*/ "fill"))
    const scaleFactor = 1 / shape.props.scale
    const pageMeta = (this.editor.getPage(pageId)! as LDPage).meta
    const layer = pageMeta.layers[shape.props.layer]
    const params = flattenParams(shape, this.editor)
    return (
      <g transform={`scale(${scaleFactor})`}>
        <CustomDrawShapeSvg
          pageId={pageId}
          hovered={false}
          isFill={shape.props.isFill}
          shape={shape}
          color={Expr.evaluate(
            shape.props.isFill
              ? (layer.layerOverrides?.fillColor ??
                  shape.props.parameterValues.fillColor)
              : (layer.layerOverrides?.strokeColor ??
                  shape.props.parameterValues.strokeColor),
            params,
          )}
          opacity={
            !Expr.evaluate(layer.visible, params)
              ? 0
              : Expr.evaluate(layer.opacity, params) *
                Expr.evaluate(shape.props.parameterValues.opacity, params)
          }
        />
      </g>
    )
  }

  override onTranslateStart(shape: CustomDrawShape):
    | void
    | ({
        id: TLShapeId
        meta?: Partial<JsonObject> | undefined
        props?: Partial<CustomDrawShapeProps> | undefined
        type: "custom-draw"
      } & Partial<Omit<CustomDrawShape, "props" | "type" | "id" | "meta">>) {
    changingBaseOrientation = true
  }

  override onRotateStart(shape: CustomDrawShape):
    | void
    | ({
        id: TLShapeId
        meta?: Partial<JsonObject> | undefined
        props?: Partial<CustomDrawShapeProps> | undefined
        type: "custom-draw"
      } & Partial<Omit<CustomDrawShape, "props" | "type" | "id" | "meta">>) {
    changingBaseOrientation = true
  }

  override onRotateEnd(
    initial: CustomDrawShape,
    current: CustomDrawShape,
  ):
    | void
    | ({
        id: TLShapeId
        meta?: Partial<JsonObject> | undefined
        props?: Partial<CustomDrawShapeProps> | undefined
        type: "custom-draw"
      } & Partial<Omit<CustomDrawShape, "props" | "type" | "id" | "meta">>) {
    if (current.props.baseOrientation && !transforming) {
      changingBaseOrientation = false
      return {
        ...current,
        props: {
          ...current.props,
          baseOrientation: {
            ...current.props.baseOrientation,
            rotation:
              current.props.baseOrientation.rotation +
              (current.rotation - initial.rotation),
          },
        },
      }
    }
  }

  override onTranslateEnd(
    initial: CustomDrawShape,
    current: CustomDrawShape,
  ):
    | void
    | ({
        id: TLShapeId
        meta?: Partial<JsonObject> | undefined
        props?: Partial<CustomDrawShapeProps> | undefined
        type: "custom-draw"
      } & Partial<Omit<CustomDrawShape, "props" | "type" | "id" | "meta">>) {
    const fillBinding =
      this.editor.getBindingsFromShape(initial, "fill")[0]?.toId &&
      this.editor.getShape(
        this.editor.getBindingsFromShape(initial, "fill")[0]?.toId,
      )
    if (current.props.baseOrientation && !transforming) {
      changingBaseOrientation = false
      return {
        ...current,
        x: fillBinding ? initial.x : current.x,
        y: fillBinding ? initial.y : current.y,
        props: {
          ...current.props,
          baseOrientation: {
            ...current.props.baseOrientation,
            x: current.props.baseOrientation.x + current.x - initial.x,
            y: current.props.baseOrientation.y + current.y - initial.y,
          },
        },
      }
    }
  }

  override onResizeStart(shape: CustomDrawShape):
    | void
    | ({
        id: TLShapeId
        meta?: Partial<JsonObject> | undefined
        props?: Partial<CustomDrawShapeProps> | undefined
        type: "custom-draw"
      } & Partial<Omit<CustomDrawShape, "props" | "type" | "id" | "meta">>) {
    changingBaseOrientation = true
  }

  override onResizeEnd(
    initial: CustomDrawShape,
    current: CustomDrawShape,
  ):
    | void
    | ({
        id: TLShapeId
        meta?: Partial<JsonObject> | undefined
        props?: Partial<CustomDrawShapeProps> | undefined
        type: "custom-draw"
      } & Partial<Omit<CustomDrawShape, "props" | "type" | "id" | "meta">>) {
    changingBaseOrientation = false
  }

  override onResize(
    shape: CustomDrawShape,
    info: TLResizeInfo<CustomDrawShape>,
  ) {
    const { scaleX, scaleY } = info

    const newSegments: TLDrawShapeSegment[] = []

    for (const segment of shape.props.segments) {
      newSegments.push({
        ...segment,
        points: segment.points.map(({ x, y, z }) => {
          return {
            x: toFixed(scaleX * x),
            y: toFixed(scaleY * y),
            z,
          }
        }),
      })
    }

    const newSegments2: TLDrawShapeSegment[] = []

    if (shape.props.baseOrientation) {
      for (const segment of shape.props.baseOrientation?.segments) {
        newSegments.push({
          ...segment,
          points: segment.points.map(({ x, y, z }) => {
            return {
              x: toFixed(scaleX * x),
              y: toFixed(scaleY * y),
              z,
            }
          }),
        })
      }
    }
    return {
      props: {
        segments: newSegments,
        baseOrientation: shape.props.baseOrientation
          ? {
              ...shape.props.baseOrientation,
              segments: newSegments2,
            }
          : undefined,
      },
    }
  }
}

function computeWinding(points: VecLike[]): "cw" | "ccw" {
  let sum = 0
  const len = points.length
  for (let i = 0; i < len; i++) {
    const a = points[i]
    const b = points[(i + 1) % len]
    sum += (b.x - a.x) * (b.y + a.y)
  }
  return sum > 0 ? "cw" : "ccw"
}

// Compare winding of two point arrays
export function haveSameWinding(a: VecLike[], b: VecLike[]): boolean {
  return computeWinding(a) === computeWinding(b)
}

function toProxyShape(
  shape: CustomDrawShape,
  isFill: boolean = false,
): TLDrawShape {
  return {
    ...shape,
    type: "draw",
    props: {
      ...shape.props,
      isClosed: isFill,
      color: "black",
      fill: isFill ? "fill" : "none",
      dash: "draw",
      size: "m",
    },
  }
}
const originalX = 400
function CustomDrawShapeSvg({
  hovered,
  isFill,
  shape,
  color,
  opacity,
  pageId,
}: {
  hovered: boolean
  isFill: boolean
  shape: CustomDrawShape
  color: string
  strokeWidth?: number
  opacity: number
  pageId: TLPageId
}) {
  const editor = useEditor()

  const parameterValues = useValue(
    "parameterValues",
    () =>
      (editor.getShape(shape.parentId) as LDGroupShape)?.props.parameterValues,
    [editor],
  )

  const proxyShape = toProxyShape(shape, isFill)

  const allPointsFromSegments = getPointsFromSegments(shape.props.segments)

  const showAsComplete =
    shape.props.isComplete || last(shape.props.segments)?.type === "straight"

  const params = flattenParams(shape, editor)

  const layers = (editor.getPage(pageId)!.meta as LDPageMeta).layers
  const layer = layers[shape.props.layer]

  const strokeWidthExpression =
    layer.layerOverrides?.strokeWidth ??
    (!shape.props.isFill && shape.props.parameterValues.strokeWidth)

  const strokeWidth =
    strokeWidthExpression && Expr.evaluate(strokeWidthExpression, params)

  // If the shape is a fill just use 0 for strokeWidth
  let sw = strokeWidth ? (strokeWidth + 1) * shape.props.scale : 0

  // if (
  //   !shape.props.isPen &&
  //   // shape.props.dash === 'draw' &&
  //   allPointsFromSegments.length === 1
  // ) {
  //   sw += rng(shape.id)() * (sw / 6)
  // }

  const options: StrokeOptions = isFill
    ? {
        thinning: 0,
        streamline: 0,
        smoothing: 0,
      }
    : getFreehandOptions(proxyShape.props, sw, showAsComplete, false)

  useEffect(() => {
    if (!shape || changingBaseOrientation) return
    const transform = shape.props.transform
    const baseOrientation = shape.props?.baseOrientation
    if (!baseOrientation) return

    const scaleX = Expr.evaluate(transform.scaleX, params)
    const scaleY = Expr.evaluate(transform.scaleY, params)

    const newSegments: TLDrawShapeSegment[] = []

    for (const segment of baseOrientation.segments) {
      newSegments.push({
        ...segment,
        points: segment.points.map(({ x, y, z }) => {
          return {
            x: toFixed(scaleX * x),
            y: toFixed(scaleY * y),
            z,
          }
        }),
      })
    }
    editor.updateShape({
      id: shape.id,
      type: shape.type,

      x:
        baseOrientation.x +
        Expr.evaluate(shape.props.transform.translateX, params),
      y:
        baseOrientation.y +
        Expr.evaluate(shape.props.transform.translateY, params),
      rotation:
        baseOrientation.rotation +
        Expr.evaluate(transform.rotate, params) * 0.0174533,
      props: {
        ...shape.props,
        segments: newSegments,
      },
    })
  }, [shape.props.transform, changingBaseOrientation])

  useEffect(() => {
    if (shape.props.clone) {
      for (let i = 1; i <= Expr.evaluate(shape.props.clone.num, params); i++) {
        editor.createShape({
          ...shape,
          id: (shape.id + i) as TLShapeId,
          x:
            shape.x +
            Expr.evaluate(shape.props.clone.transformation.translateX, params) *
              i,
          y:
            shape.y +
            Expr.evaluate(shape.props.clone.transformation.translateY, params) *
              i,

          props: {
            ...shape.props,
            clone: undefined,
            isClone: true,
          },
          isLocked: true,
        })
      }
      if (shape.props.clone) {
        const clones = editor
          .getCurrentPageShapes()
          .filter((s) => s.id.startsWith(shape.id) && s.id !== shape.id)

        clones
          .filter((sh) => {
            const n = Number(sh.id.substring(shape.id.length))
            // console.log({ n })

            // console.log(
            //   "num",
            //   Expr.evaluate((shape.props.clone as CloneOptions).num, params),
            // )
            // return (
            //   n > Expr.evaluate((shape.props.clone as CloneOptions).num, params)
            // )
          })
          .forEach((shap) => {
            //  console.log("deleting shape", shap)
            editor.updateShape({
              ...shap,
              isLocked: false,
            })
            editor.deleteShape(shap.id)
          })
      }
    }
  }, [shape.props.clone, color, sw, opacity])

  const visibility = Expr.evaluate(layer.visible, params)
  //if (!visibility) return

  return (
    <>
      <path
        fillRule={
          isFill
            ? shape.props.segments.length > 1
              ? "evenodd"
              : "nonzero"
            : "nonzero"
        }
        opacity={opacity}
        d={
          isFill
            ? getSvgPathFromMultipleSegments(
                getStrokePointsFromSegments(
                  shape.props.segments
                    .map((segment) => segment.points)
                    .filter((points) => points.length > 0),
                  options,
                ),
                true,
              )
            : svgInkForSegments(
                shape.props.segments
                  .map((segment) => segment.points)
                  .filter((points) => points.length > 0),
                options,
              )
        }
        strokeLinecap="round"
        fill={color}
      />
      {hovered && (
        <path
          fillRule={
            isFill
              ? shape.props.segments.length > 1
                ? "evenodd"
                : "nonzero"
              : "nonzero"
          }
          opacity={hovered ? 0.07 : 0}
          d={
            true
              ? getSvgPathFromMultipleSegments(
                  getStrokePointsFromSegments(
                    shape.props.segments
                      .map((segment) => segment.points)
                      .filter((points) => points.length > 2),
                  ),
                  true,
                )
              : svgInkForSegments(
                  shape.props.segments
                    .map((segment) => segment.points)
                    .filter((points) => points.length > 2),
                  options,
                )
          }
          //stroke={color}
          //strokeWidth={sw}
          fill={"gray"}
        />
      )}
    </>
  )
}

export function getPointsFromSegments(segments: TLDrawShapeSegment[]) {
  const points: Vec[] = []

  for (const segment of segments) {
    if (segment.type === "free" || segment.points.length < 2) {
      points.push(...segment.points.map(Vec.Cast))
    } else {
      const pointsToInterpolate = Math.max(
        4,
        Math.floor(Vec.Dist(segment.points[0], segment.points[1]) / 16),
      )
      points.push(
        ...Vec.PointsBetween(
          segment.points[0],
          segment.points[1],
          pointsToInterpolate,
        ),
      )
    }
  }

  return points
}
